import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import {
  changeOrganizationSubscription,
  getOrganizationSubscription,
  getPlatformSettings,
  listPlans,
  OrganizationSubscription,
  PlatformKey,
  SubscriptionChange,
  SubscriptionPlan,
} from "../../services/superadminService";
import { getErrorMessage } from "../../utils/apiResponse";
import {
  PaymentStatusChip,
  SubscriptionStatusChip,
  describeSubscriptionEnd,
  formatMoney,
} from "./SubscriptionChips";
import { downloadSubscriptionInvoice } from "../../utils/subscriptionInvoicePdf";
import { SaCard } from "./saUi";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";

const QUICK_EXTENSIONS = [30, 90, 365];

const SOURCE_LABELS: Record<string, string> = {
  TRIAL: "Trial",
  MANUAL: "Manual",
  WHISH: "Whish",
};

const formatDate = (value: string | null) =>
  value ? format(new Date(value), "d MMM yyyy") : "Unlimited";

const money = (amount: number | null, currency: string | null) =>
  amount === null ? "—" : `${amount.toLocaleString()} ${currency ?? ""}`.trim();

interface PendingChange {
  title: string;
  description: string;
  change: SubscriptionChange;
}

interface SubscriptionPanelProps {
  organizationId: string;
  organizationName: string;
  platform: PlatformKey;
  /** Lets the page refresh its header chip after a change. */
  onChanged?: (subscription: OrganizationSubscription) => void;
}

/**
 * Everything the superadmin does to one tenant's subscription: quick
 * extensions, a custom end date, assigning a package, and the full history.
 * Every action asks for confirmation, because it locks or unlocks a
 * customer's backoffice the moment it lands.
 */
const SubscriptionPanel: React.FC<SubscriptionPanelProps> = ({
  organizationId,
  organizationName,
  platform,
  onChanged,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState<OrganizationSubscription | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [trialDays, setTrialDays] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [note, setNote] = useState("");
  const [customDate, setCustomDate] = useState("");
  const [planId, setPlanId] = useState("");
  const [amount, setAmount] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subscription, allPlans, settings] = await Promise.all([
        getOrganizationSubscription(organizationId),
        listPlans(),
        getPlatformSettings(),
      ]);
      setData(subscription);
      setPlans(allPlans);
      setTrialDays(settings.trialDays?.[platform] ?? null);
    } catch {
      // The interceptor already surfaced the message.
    }
    setLoading(false);
  }, [organizationId, platform]);

  useEffect(() => {
    load();
  }, [load]);

  // Only packages this tenant could actually be sold.
  const availablePlans = useMemo(
    () =>
      plans.filter(
        (plan) =>
          plan.isActive && (plan.platform === null || plan.platform === platform)
      ),
    [plans, platform]
  );

  const selectedPlan = availablePlans.find((plan) => plan.id === planId);

  const ask = (title: string, description: string, change: SubscriptionChange) => {
    setNote("");
    setPending({ title, description, change });
  };

  const apply = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      const result = await changeOrganizationSubscription(organizationId, {
        ...pending.change,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setData(result);
      onChanged?.(result);
      toast.success(`Subscription updated for ${organizationName}.`, {
        position: "top-right",
      });
      setPending(null);
      setPlanId("");
      setAmount("");
      setCustomDate("");
    } catch (err: any) {
      toast.error(getErrorMessage(err, "Could not update the subscription."), {
        position: "top-right",
      });
    }
    setSaving(false);
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!data) return null;

  const current = data.current;
  const extendsFrom =
    current.status === "EXPIRED" || !current.endsAt
      ? "today"
      : `the current end date (${formatDate(current.endsAt)})`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <SaCard
        title="Current subscription"
        subtitle="Extend it, end it, or assign a package paid outside Whish."
        icon={<WorkspacePremiumOutlinedIcon />}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <SubscriptionStatusChip summary={current} />
          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
            {current.plan?.name ?? (current.isTrial ? "Free trial" : "No package")}
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: COLORS.placeHolderColor }}>
            {describeSubscriptionEnd(current)}
          </Typography>
        </Box>

        <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", mb: 1 }}>
          Extend
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          {trialDays !== null && trialDays > 0 && (
            <Button
              variant="outlined"
              size="small"
              sx={{ textTransform: "none", minHeight: 36 }}
              onClick={() =>
                ask(
                  `Give a ${trialDays}-day trial?`,
                  `Adds ${trialDays} trial days on top of ${extendsFrom}.`,
                  { extendDays: trialDays, isTrial: true }
                )
              }
            >
              +{trialDays}d trial
            </Button>
          )}
          {QUICK_EXTENSIONS.map((days) => (
            <Button
              key={days}
              variant="outlined"
              size="small"
              sx={{ textTransform: "none", minHeight: 36 }}
              onClick={() =>
                ask(
                  `Extend by ${days} days?`,
                  `Adds ${days} days on top of ${extendsFrom}.`,
                  { extendDays: days }
                )
              }
            >
              +{days} days
            </Button>
          ))}
          <Button
            variant="outlined"
            size="small"
            sx={{ textTransform: "none", minHeight: 36 }}
            onClick={() =>
              ask(
                "Remove the end date?",
                `${organizationName} will never be locked out until you set a new end date.`,
                { endsAt: null }
              )
            }
          >
            Make unlimited
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            sx={{ textTransform: "none", minHeight: 36 }}
            onClick={() =>
              ask(
                "End the subscription now?",
                `${organizationName}'s staff are locked out right away and see the packages screen. Their clients can still book.`,
                { endsAt: new Date().toISOString() }
              )
            }
          >
            End now
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <TextField
            type="date"
            size="small"
            label="Custom end date"
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 190 }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!customDate}
            sx={{ textTransform: "none", minHeight: 36 }}
            onClick={() => {
              // End of the chosen day, in the superadmin's own timezone.
              const endsAt = new Date(`${customDate}T23:59:59`).toISOString();
              ask(
                `Set the end date to ${format(new Date(endsAt), "d MMM yyyy")}?`,
                "Replaces the current end date, whatever it is.",
                { endsAt }
              );
            }}
          >
            Set date
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", mb: 1 }}>
          Assign a package
        </Typography>
        {availablePlans.length === 0 ? (
          <Typography sx={{ fontSize: "0.85rem", color: COLORS.placeHolderColor }}>
            No active packages for this platform yet. Create one on the Packages
            page.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              select
              size="small"
              label="Package"
              value={planId}
              onChange={(e) => {
                setPlanId(e.target.value);
                const plan = availablePlans.find((p) => p.id === e.target.value);
                setAmount(plan ? String(plan.price) : "");
              }}
              sx={{ minWidth: 220 }}
            >
              {availablePlans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.name} · {plan.price} {plan.currency} / {plan.durationDays}d
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={`Amount paid${selectedPlan ? ` (${selectedPlan.currency})` : ""}`}
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0, step: "0.01" }}
              sx={{ width: 170 }}
            />
            <Button
              variant="contained"
              size="small"
              disabled={!selectedPlan}
              sx={{ textTransform: "none", minHeight: 36 }}
              onClick={() => {
                if (!selectedPlan) return;
                const paid = amount.trim() === "" ? undefined : Number(amount);
                ask(
                  `Assign ${selectedPlan.name}?`,
                  `Adds ${selectedPlan.durationDays} days on top of ${extendsFrom}${
                    paid !== undefined ? ` and records ${paid} ${selectedPlan.currency} as paid` : ""
                  }.`,
                  {
                    planId: selectedPlan.id,
                    ...(paid !== undefined && !Number.isNaN(paid)
                      ? { amount: paid, currency: selectedPlan.currency }
                      : {}),
                  }
                );
              }}
            >
              Apply package
            </Button>
          </Box>
        )}
      </SaCard>

      <SaCard
        title="Payments & invoices"
        subtitle="Whish payments for packages. Each one is also the organization's invoice."
        icon={<ReceiptLongOutlinedIcon />}
        tone="green"
        flush
      >
        {(data.payments ?? []).length === 0 ? (
          <Typography sx={{ p: 2, fontSize: "0.85rem", color: COLORS.placeHolderColor }}>
            No Whish payments yet.
          </Typography>
        ) : (
          (data.payments ?? []).map((payment, index) => (
            <Box key={payment.id}>
              {index > 0 && <Divider />}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, flexWrap: "wrap" }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                    #{payment.invoiceNumber} · {payment.planName}
                  </Typography>
                  <Typography sx={{ fontSize: "0.76rem", color: COLORS.placeHolderColor }}>
                    {format(new Date(payment.paidAt ?? payment.createdAt), "d MMM yyyy, HH:mm")}
                    {payment.periodEndsAt ? ` · covers until ${formatDate(payment.periodEndsAt)}` : ""}
                    {payment.failureReason ? ` · ${payment.failureReason}` : ""}
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", fontVariantNumeric: "tabular-nums" }}>
                  {formatMoney(payment.amount, payment.currency)}
                </Typography>
                <PaymentStatusChip status={payment.status} />
                {payment.status === "PAID" && (
                  <Button
                    size="small"
                    onClick={() => downloadSubscriptionInvoice(payment, organizationName)}
                    sx={{ textTransform: "none" }}
                  >
                    Invoice PDF
                  </Button>
                )}
              </Box>
            </Box>
          ))
        )}
      </SaCard>

      <SaCard
        title="History"
        subtitle="Every change to this organization's end date."
        icon={<HistoryOutlinedIcon />}
        tone="slate"
        flush
      >
        {data.history.length === 0 ? (
          <Typography sx={{ p: 2, fontSize: "0.85rem", color: COLORS.placeHolderColor }}>
            No changes yet. This organization predates subscriptions and has no
            end date.
          </Typography>
        ) : isMobile ? (
          data.history.map((row, index) => (
            <Box key={row.id}>
              {index > 0 && <Divider />}
              <Box sx={{ px: 2, py: 1.25 }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  {SOURCE_LABELS[row.source] ?? row.source}
                  {row.plan ? ` · ${row.plan.name}` : ""} → {formatDate(row.endsAt)}
                </Typography>
                <Typography sx={{ fontSize: "0.76rem", color: COLORS.placeHolderColor }}>
                  {format(new Date(row.createdAt), "d MMM yyyy, HH:mm")}
                  {row.amount !== null ? ` · ${money(row.amount, row.currency)}` : ""}
                  {row.note ? ` · ${row.note}` : ""}
                </Typography>
              </Box>
            </Box>
          ))
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#fafafa" }}>
                  <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Package</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>New end date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Amount
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.history.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {format(new Date(row.createdAt), "d MMM yyyy, HH:mm")}
                    </TableCell>
                    <TableCell>{SOURCE_LABELS[row.source] ?? row.source}</TableCell>
                    <TableCell>{row.plan?.name ?? "—"}</TableCell>
                    <TableCell>{formatDate(row.endsAt)}</TableCell>
                    <TableCell align="right">{money(row.amount, row.currency)}</TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>{row.note ?? "—"}</TableCell>
                    <TableCell sx={{ color: COLORS.placeHolderColor }}>
                      {row.createdBy ? `+${row.createdBy}` : "Signup"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SaCard>

      <Dialog
        open={!!pending}
        onClose={() => !saving && setPending(null)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{pending?.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", mb: 2 }}>
            {pending?.description}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Note (optional)"
            placeholder="e.g. paid cash, 3 months"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setPending(null)}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={apply}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Saving…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionPanel;
